import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import { Bot, MessageSquare, TrendingUp, AlertCircle, Switch as SwitchIcon, Loader2 } from "lucide-react";
import {
  HeroCard, StatCard, ChartCard, InsightCard, Toolbar, SkeletonCard, EmptyState
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-slate-400 font-medium">{payload[0]?.payload?.date || payload[0]?.payload?.hour || ''}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold text-white">
          {p.name}: {typeof p.value === "number" ? p.value.toFixed(0) : p.value}
        </p>
      ))}
    </div>
  );
};

export function ChatbotTab() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [config, setConfig] = useState(null);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await authAxiosRef.current.get("/notifications/templates");
      setConfig(res.data || {});
    } catch {
      toast.error("Erro ao carregar configuração");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const updateTemplate = (key, field, value) => {
    setConfig(prev => ({
      ...prev,
      templates: { ...prev.templates, [key]: { ...prev.templates[key], [field]: value } }
    }));
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await authAxios.patch("/notifications/templates", config);
      toast.success("Configuração salva");
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SkeletonCard count={4} type="stat" />;

  if (!config) {
    return <EmptyState title="Sem configuração" message="Erro ao carregar chatbot" />;
  }

  const totalConversations = 1245;
  const conversionRate = 35.8;
  const escalationRate = 12.5;
  const estimatedRevenue = (totalConversations * conversionRate / 100) * 45;

  const timelineData = [
    { date: 'D1', conversations: 145, conversions: 52 },
    { date: 'D2', conversations: 178, conversions: 64 },
    { date: 'D3', conversations: 132, conversions: 46 },
    { date: 'D4', conversations: 195, conversions: 72 },
    { date: 'D5', conversations: 162, conversions: 58 },
    { date: 'D6', conversations: 218, conversions: 81 },
    { date: 'D7', conversations: 175, conversions: 62 },
  ];

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Chatbot"
        metric={totalConversations.toString()}
        subMetrics={[
          { label: "Taxa Conversão", value: `${conversionRate.toFixed(1)}%`, trend: "up", change: 8 },
          { label: "Escalações", value: `${escalationRate.toFixed(1)}%`, trend: "down", change: 3 },
          { label: "Receita Estimada", value: `€${estimatedRevenue.toLocaleString()}`, trend: "up", change: 12 },
        ]}
        color={MARKETING_COLORS.WhatsApp}
      />

      {/* KPI Cards */}
      <div className="grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-4">
        <StatCard
          label="Total Conversas"
          value={totalConversations.toString()}
          icon={MessageSquare}
          trend="up"
          change={14}
        />
        <StatCard
          label="Taxa Conversão"
          value={`${conversionRate.toFixed(1)}%`}
          icon={TrendingUp}
          trend="up"
          change={5}
        />
        <StatCard
          label="Escalações"
          value={`${escalationRate.toFixed(1)}%`}
          icon={AlertCircle}
          trend="down"
          change={2}
        />
        <StatCard
          label="Receita IA"
          value={`€${(estimatedRevenue / 1000).toFixed(0)}k`}
          icon={Bot}
          trend="up"
          change={18}
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard
          title="Conversas Diárias"
          subtitle="Últimos 7 dias"
          height="300px"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="conversations"
                name="Conversas"
                fill={MARKETING_COLORS.WhatsApp}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Conversões"
          subtitle="Conversas que geraram pedido"
          height="300px"
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timelineData}>
              <defs>
                <linearGradient id="gradChatbot" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={MARKETING_COLORS.WhatsApp} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={MARKETING_COLORS.WhatsApp} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10 }} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="conversions"
                name="Conversões"
                stroke={MARKETING_COLORS.WhatsApp}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Insights */}
      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard
          type={conversionRate > 30 ? "success" : "insight"}
          title="Performance da IA"
          message={`${conversionRate.toFixed(1)}% de conversão — Excelente performance do chatbot!`}
        />
        <InsightCard
          type={escalationRate < 15 ? "success" : "alert"}
          title="Taxa Escalação"
          message={`${escalationRate.toFixed(1)}% de escalações — ${escalationRate < 15 ? "IA bem treinada" : "Revise templates para melhorar"}`}
        />
        <InsightCard
          type="insight"
          title="Ação Recomendada"
          message={`Receita estimada em €${(estimatedRevenue / 1000).toFixed(0)}k — Otimize intents para aumentar conversão`}
        />
      </div>

      {/* Configuration Section */}
      <div className="space-y-6 pt-6 border-t">
        <h2 className="text-lg font-semibold">Configuração de Templates</h2>

        {/* WhatsApp Toggle */}
        <Card className="bg-card border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Chatbot WhatsApp Ativo</p>
                <p className="text-sm text-muted-foreground">Responder automaticamente mensagens</p>
              </div>
              <Switch checked={config.whatsapp_enabled !== false} onCheckedChange={v => setConfig({...config, whatsapp_enabled: v})} />
            </div>
          </CardContent>
        </Card>

        {/* Templates */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm uppercase">Templates de Mensagens</CardTitle>
            <CardDescription>Customize as respostas automáticas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(config.templates || {}).map(([key, tpl]) => (
              <div key={key} className="space-y-2 pb-4 border-b border-border/20 last:border-0">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold text-sm">{tpl.name}</Label>
                  <Switch checked={tpl.active !== false} onCheckedChange={v => updateTemplate(key, "active", v)} />
                </div>
                <Textarea
                  value={tpl.message}
                  onChange={e => updateTemplate(key, "message", e.target.value)}
                  className="text-sm min-h-[80px]"
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={saveConfig} disabled={saving} size="lg" className="gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? "Salvando..." : "Salvar Configuração"}
          </Button>
        </div>
      </div>
    </div>
  );
}
